import { createSelector } from "@reduxjs/toolkit";

import { StoreState } from "~/redux/reducers";

import { workstationApi } from "~/redux/warehouse/workstation";

import {
  selectPortStatusByIdMap,
  selectHandRaisedByIdMap,
  selectPortOpenByIdMap,
  selectPortBinsByIdMap,
  selectGridModeByIdMap
} from "./andon.slice";
import type { AndonStatus, PongData, AndonEventWithMeta } from "./andon.types";

const EMPTY_ARRAY: number[] = [];

export const selectWorkstationStatus = (workstationId: string) =>
  createSelector(
    [
      (state: StoreState) => state.andon.portStatusById,
      (state: StoreState) => state
    ],
    (portStatusById, state): AndonStatus => {
      const workstation = workstationApi.endpoints.getWorkstations
        .select()(state)
        .data?.find((ws) => ws.id === workstationId);

      if (!workstation) return "good";

      const portIds = [
        ...workstation.ports.map((p) => p.portId.toString()),
        ...(workstation.ports
          .map((p) => p.parentPortId?.toString())
          .filter(Boolean) as string[])
      ];

      return portIds.some((pid) => portStatusById[pid] === "bad")
        ? "bad"
        : "good";
    }
  );

export const selectPortStatus =
  (portId: string) =>
  (state: StoreState): AndonStatus =>
    selectPortStatusByIdMap(state)[portId] ?? "good";

export const selectPortIsOpen =
  (portId: string) =>
  (state: StoreState): boolean =>
    selectPortOpenByIdMap(state)[portId] ?? false;

export const selectPortHasBins =
  (portId: string) =>
  (state: StoreState): number[] =>
    selectPortBinsByIdMap(state)[portId] ?? EMPTY_ARRAY;

export const selectGridSystemMode = (gridId: string) =>
  createSelector(
    (state: StoreState) => selectGridModeByIdMap(state),
    (modes) => modes[gridId] ?? null
  );

export const selectPongLoopHistory =
  (workstationId: string) =>
  (
    state: StoreState
  ): { pongData: PongData; loopTime: number; timestamp: string }[] =>
    state.andon.workstationPongHistory[workstationId] ?? [];

export const selectCompactViewStatuses = (ids: string[]) =>
  createSelector(
    (state: StoreState) => state,
    (state): Record<string, AndonStatus> =>
      Object.fromEntries(
        ids.map((id) => [id, selectWorkstationStatus(id)(state)])
      )
  );

export const selectEventHistory = (state: StoreState) =>
  state.andon.eventHistory;

export const selectEventHistoryByWorkstationIdMap = (ids: string[]) =>
  createSelector(selectEventHistory, (events) => {
    const grouped: Record<string, AndonEventWithMeta[]> = {};

    for (const id of ids) {
      grouped[id] = [];
    }

    for (const evt of events) {
      if (evt.workstationId && grouped[evt.workstationId]) {
        grouped[evt.workstationId].push(evt);
      }
    }

    return grouped;
  });

export const selectLooseThreadEvents = createSelector(
  (state: StoreState) => state.andon.eventHistory,
  (events): AndonEventWithMeta[] => events.filter((evt) => !evt.workstationId)
);

export const selectLayoutCode = createSelector(
  (state: StoreState) => state.andon.boardLayout,
  (state: StoreState) => state.andon.boardDimensions,
  (layout, dimensions) =>
    JSON.stringify({
      layout,
      dimensions
    })
);

export const selectHandRaiseStartTime = (id: string) => (state: StoreState) =>
  state.andon.handRaisedByWorkstationId[id]?.timestamp ?? null;

export const selectHandRaisedForWorkstation = (workstationId: string) =>
  createSelector(
    selectHandRaisedByIdMap,
    (map) => map[workstationId] ?? { raised: false, timestamp: null }
  );
